import { Component, ViewEncapsulation, ViewChild, OnInit, OnDestroy, inject, effect } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ItemNode, FlatNode, ChipFilter } from 'src/models/schedule.model';

import { BookJob } from 'src/providers/book-job/book-job';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { ToDosPage } from 'src/pages/to-dos/to-dos';
import { ToDosAddListPage } from 'src/pages/to-dos/add-list/add-list';
import { ToDosListSettingsPage } from 'src/pages/to-dos/list-settings/list-settings';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { BREAK_LARGE_RESOLUTION } from 'android/app/src/constants/app.contants';
import { Router } from '@angular/router';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Events } from 'src/providers/events/events';

const ORPHANED_PROPERTY_FILTER_VALUE = 'orphaned';

@Component({
    templateUrl: 'all-to-do-lists.html',
    styleUrls: ['all-to-do-lists.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class AllToDoListsPage implements OnInit, OnDestroy {
  addressId: any;
  addressResponse: any;
  addressItems: any;
  addressFilter: any;
  currentRows: any;
  errorMessage: string;
  form: UntypedFormGroup;
  isSearching: boolean;
  isGettingLists: boolean;
  searchTerm: string;
  loaded: boolean;
  panelClosedSubscription: Subscription;
  pageSize: any;
  pageIndex: any;
  headers: any = ['Name', 'Description', 'Default for', 'Remote Inspection', 'last-column'];
  routeReused: boolean;
  rows: any = [];
  showLoadingSpinner: boolean;
  services: any;
  searchForm: UntypedFormGroup;
  totalRows: any;
  toDoLists: any;
  showAboutThisPage: boolean;
  hasProperties: boolean;
  @ViewChild('tidyTable') tidyTable: any;
  backPage = 'more';
  private checklistUpdatedSubscription: Subscription;

  private _transformer = (node: ItemNode, level: number) => ({
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level,
      data: node,
    });

  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  chips: ChipFilter[] = [];
  selectedFilters: {
    propertyName: any[];
    orphaned: any[];
  } = {
    propertyName: [],
    orphaned: []
  };
  filterNamesReferences = {
    'Properties': 'propertyName',
    'Other': 'orphaned'
  };
  private resizeHandler: () => void;

  // TanStack Query service - use signal to react to list changes
  private readonly toDosQueries = inject(ToDosQueriesService);
  private aboutThisPageService = inject(AboutThisPageService);
  private listsVersionEffect = effect(() => {
    // Watch the listsVersion signal - when it changes, reload the table
    const version = this.toDosQueries.listsVersion();
    // Skip the initial value (0) to avoid loading twice on init
    if (version > 0 && this.loaded) {
      this.pageIndex = 1;
      this.loadTable();
    }
  });

  constructor(
    private bookJob: BookJob,
    private client: Client,
    private fb: FormBuilder,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public toDos: ToDos,
    private util: Util,
    public windowService: WindowService,
    public onboardingProvider: OnboardingProvider,
    private onboardingDummyDataService: OnboardingDummyDataService,
    private router: Router,
    private agentsProvider: AgentsProvider,
    private events: Events,
  ) {
    this.form = this.fb.group({
      property: ['', Validators.required],
    });
    this.searchForm = this.fb.group({
      search: [''],
    });
    this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val) => this.updateSearch(val));
  }

  async ngOnInit() {
    try {
      const cameFrom = localStorage.getItem('cameFromPage');
      if (cameFrom === 'properties' || cameFrom === 'more') {
        this.backPage = cameFrom;
      }
      // Subscribe to checklist updates to refresh the table when lists are added/modified
      this.checklistUpdatedSubscription = this.events.subscribe('checklist:updated', () => {
        this.pageIndex = 1;
        this.loadTable();
      });
      // No resize handler needed - checklists page works on all screen sizes
      this.resizeHandler = () => {
        // Reserved for future resize handling if needed
      };
      this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true, skipOnboardingCheck = false) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('checklists');
    this.pageSize = 25;
    this.pageIndex = 1;
    await this.getPropertyItems();
    this.services = await this.bookJob.fetchAllRequiredTeamServices();
    this.buildHeaders();
    this.form.patchValue({property: 'all'});
    await this.loadTable();
    this.loaded = true;
    this.showLoadingSpinner = false;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/checklists').then(async () => {
        this.routeReused = true;
        await this.loadPage(true);
      });
    }
  }

  dismissAboutThisPage(): void {
    this.showAboutThisPage = false;
    this.rows = [];
    this.currentRows = [];
    this.loadPage(true);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  ionViewWillLeave() {
    // Reserved for future cleanup if needed
  }

  cleanup() {
    if (this.panelClosedSubscription) {
      this.panelClosedSubscription.unsubscribe();
      this.storage.delete('shouldReloadPageAfterClosingSideRail');
    }
    if (this.checklistUpdatedSubscription) {
      this.checklistUpdatedSubscription.unsubscribe();
    }
  }

  async getPropertyItems() {
    this.addressResponse = await this.client.getAddresses();
    this.addressId = this.client.getSelectedAddressId(this.addressResponse);
    this.addressItems = this.client.parseAddressList(this.addressResponse, true);
    this.addressItems.unshift({
      value: 'all',
      viewValue: new TranslationPipe().transform('All Properties')
    })
    this.addressItems.pop();
    this.getTreeData();
  }

  getTreeData(): void {
    const treeData = [
      {
        name: 'Properties',
        children: this.addressItems.filter(item => item.value !== 'all').map(address => ({
          name: address.viewValue,
          value: address.value,
          checked: false,
          parentId: 0
        }))
      },
      {
        name: 'Other',
        children: [
          {
            name: new TranslationPipe().transform('Lists with no property'),
            value: ORPHANED_PROPERTY_FILTER_VALUE,
            checked: false,
            parentId: 1
          }
        ]
      }
    ];
    treeData[0].children.forEach((child) => {
      child.parentId = 0;
    });
    this.dataSource.data = treeData;
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  checkNode(node: ItemNode, event: MatCheckboxChange): void {
    node.checked = event.checked;
    this.updateFilters(node, event.checked);
  }

  getNodeVariables(node: ItemNode) {
    const foundParentName = this.dataSource.data[node.data.parentId].name;
    const foundNodeValue = node.data.value;
    const matchedParentName = this.filterNamesReferences[foundParentName];
    return { foundParentName, foundNodeValue, matchedParentName };
  }

  updateFilters(node: ItemNode, isEnabled: boolean): void {
    if (!node?.data?.parentId && node?.data?.parentId !== 0) {
      return;
    }
    const { foundParentName, foundNodeValue, matchedParentName } =
      this.getNodeVariables(node);
    const chip: ChipFilter = {
      displayParentName: foundParentName,
      parentName: matchedParentName,
      name: node.name,
      value: foundNodeValue,
      icon: 'home',
      color: this.getChipColor(foundNodeValue),
    };
    this.pageIndex = 1;
    if (isEnabled) {
      this.addChip(chip);
    } else {
      this.removeChip(chip);
    }
  }

  private getChipColor(value: any): string {
    const address = this.addressResponse?.find((a) => a.id === value);
    return address?.color || '#1E2125';
  }

  addChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.selectedFilters) {
      this.selectedFilters = {
        propertyName: [],
        orphaned: []
      };
    }
    this.selectedFilters[chip.parentName].push(chip.value);
    this.chips.push(chip);
    this.loadTable();
    this.updateTree();
  }

  updateTree(): void {
    this.dataSource.data.forEach((parent) => {
      parent.children.forEach((child) => {
        if (
          this.selectedFilters[
            this.filterNamesReferences[parent.name]
          ]?.includes(child.value)
        ) {
          child.checked = true;
        } else {
          child.checked = false;
        }
      });
    });
  }

  removeChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) {
      return;
    }
    this.selectedFilters[chip.parentName] = this.selectedFilters[
      chip.parentName
    ].filter((value) => value !== chip.value);
    this.chips = this.chips.filter(
      (item) => item.value !== chip.value
    );
    this.loadTable();
    this.updateTree();
  }

  getNodeChecked(node: ItemNode): boolean {
    if (!node?.data?.parentId && node?.data?.parentId !== 0) {
      return false;
    }
    const { foundParentName, foundNodeValue, matchedParentName } = this.getNodeVariables(node);
    return this.selectedFilters?.[matchedParentName]?.includes(foundNodeValue);
  }

  async getToDoLists() {
    const onlyOrphaned = this.selectedFilters.orphaned.length > 0;
    const property = !onlyOrphaned && this.selectedFilters.propertyName.length > 0
      ? this.selectedFilters.propertyName
      : null;
    const didFinishToDosOnboarding = await this.onboardingProvider.checkIfDidFinishToDosOnboarding();
    const toDosMockData = this.onboardingDummyDataService.getToDosMockData();
    const toDoLists = this.showAboutThisPage && !didFinishToDosOnboarding ? {body: toDosMockData, totalRecords: 25} : await this.toDos.getAllToDoLists(this.pageSize, this.pageIndex, property, onlyOrphaned, this.searchTerm);
    this.toDoLists = toDoLists.body;
    this.totalRows = toDoLists.totalRecords;
  }

  buildHeaders(): void {
    this.headers = [
      'Name',
      'Description',
      'Default for',
      'Remote Inspection',
      'last-column'
    ];
  }

  async loadTable(showLoadingSpinner = true) {
    let loading = null;
    if (this.loaded && !this.routeReused && showLoadingSpinner) {
      loading = await this.util.showLoading();
    }
    await this.getToDoLists();
    this.buildRows(showLoadingSpinner);
    loading?.dismiss();

    const hasToDoLists = this.toDoLists && this.toDoLists.length > 0;
  }

  buildRows(resetRows = true) {
    if (resetRows) {
      this.rows = [];
    } else {
      this.rows = [...this.rows];
    }
    this.toDoLists.map((list) => {
      this.rows.push({
        id: list.id,
        addressId: list.address_ids[0],
        'Name': {
          value: list.title,
          action: null,
        },
        'Description': {
          value: list.description || '',
          action: null,
          truncate: 80,
        },
        'Default for': {
          value: this.getListDefaultSettings(list),
          action: null,
          truncate: 60,
        },
        'Remote Inspection': this.getRemoteInspectionCell(list.before_after_photos_state),
        'last-column': {
          value: '',
          action: null,
        }
      });
    });
    this.currentRows = this.rows;
  }

  async changeProperty() {
    this.pageIndex = 1;
    await this.loadTable();
  }

  getAddressName(addressId) {
    const address = this.addressItems.find((addr => addr.value === parseInt(addressId)));
    return address?.viewValue;
  }

  getListDefaultSettings(list) {
    let settings = '';
    list.default_list_settings.map((setting) => {
      const key = setting.key;
      if (key.includes('account')) {
        settings += 'Account, ';
      } else if (!key.includes('address')) {
        const serviceKey = key.split('.').slice(1).join('.');
        settings += `${this.getServiceNameFromKey(serviceKey)  }, `;
      } else if (key.split('.').length === 5) {
        const addressId = key.split('.')[2];
        const serviceKey = key.split('.').slice(3).join('.');
        const addressName = this.getAddressName(addressId);
        if (addressName) {
          settings += `${addressName  } (${  this.getServiceNameFromKey(serviceKey)  }), `;
        }
      } else {
        const addressId = key.split('.')[2];
        const addressName = this.getAddressName(addressId);
        if (addressName) {
          settings += `${addressName  }, `;
        }
      }
    });
    return settings.slice(0, -2).split(', ').map(s => s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')).join(', ');
  }

  getServiceNameFromKey(serviceKey) {
    const service = this.services.find((svc => svc.key === serviceKey));
    return service?.name;
  }

  getRemoteInspectionCell(state: string) {
    const t = (key: string) => new TranslationPipe().transform(key);
    const config = {
      photo: { icon: 'assets/svg/camera-outline.svg', label: 'B&A', tooltip: 'Before & After Photos' },
      video: { icon: 'assets/svg/videocam-outline.svg', label: 'B&A', tooltip: 'Before & After Videos' },
      before_photo_only: { icon: 'assets/svg/camera-outline.svg', label: 'Before', tooltip: 'Before Photos' },
      after_photo_only: { icon: 'assets/svg/camera-outline.svg', label: 'After', tooltip: 'After Photos' },
      before_video_only: { icon: 'assets/svg/videocam-outline.svg', label: 'Before', tooltip: 'Before Videos' },
      after_video_only: { icon: 'assets/svg/videocam-outline.svg', label: 'After', tooltip: 'After Videos' },
      inactive: { icon: null, label: 'None', tooltip: 'No Inspection' },
    };
    const cfg = config[state] || { icon: null, label: 'OFF', tooltip: 'OFF' };
    return {
      value: t(cfg.label),
      icon: cfg.icon,
      action: null,
      truncate: t(cfg.tooltip),
    };
  }

  sortChanged(sort) {
    if (sort.direction === 'asc' || sort.direction === '') {
      this.currentRows.sort((a, b) =>
        a[sort.active].value > b[sort.active].value ? 1 : -1
      );
      this.currentRows = this.rows;
    } else {
      this.currentRows.sort((a, b) =>
        a[sort.active].value < b[sort.active].value ? 1 : -1
      );
      this.currentRows = this.rows;
    }
  }

  goToToDoList(list) {
    try {
      if (this.showAboutThisPage) {
        return;
      }
      localStorage.setItem('addressId', list?.element?.addressId || list.address_id);
      localStorage.setItem('taskListId', list?.element?.id || list.id);
      const params = {
        selectedList: list
      }
      this.storage.save('dialog-params', params);

      // On mobile, navigate to page directly instead of using right-side panel
      if (!this.windowService.isDesktopRes) {
        this.navCtrl.navigateForward('to-dos');
        return;
      }

      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(ToDosPage);
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        const shouldReloadPageAfterClosingSideRail = await this.storage.retrieve('shouldReloadPageAfterClosingSideRail');
        if (shouldReloadPageAfterClosingSideRail) {
          this.pageIndex = 1;
          await this.storage.save('shouldReloadPageAfterClosingSideRail', false);
          this.loadTable();
        }
      });
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  async goToDefaultSettingsPage() {
    if (this.showAboutThisPage) {
      return;
    }
    try {
      const params = {
        addressItems: this.addressItems
      }
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      this.rightSidePanelService.openRightSidePanel(ToDosListSettingsPage);
      this.rightSidePanelService.setDialogPageTitle('Default Checklists');
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        const shouldReloadPageAfterClosingSideRail = await this.storage.retrieve('shouldReloadPageAfterClosingSideRail');
        if (shouldReloadPageAfterClosingSideRail) {
          await this.storage.save('shouldReloadPageAfterClosingSideRail', false);
          this.loadTable();
        }
      });
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  goToAddToDoList() {
    if (this.showAboutThisPage) {
      return;
    }
    try {
      const params = {
        toDoLists: this.toDoLists,
        addressFilter: this.addressItems.filter((address) => address.value !== 'all'),
        hasToDoLists: true
      }
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      this.rightSidePanelService.openRightSidePanel(ToDosAddListPage);
      this.rightSidePanelService.setDialogPageTitle('Add Checklist');
    } catch (err) {
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  async updateSearch({ search }) {
    const term = search.length < 2 ? '' : search;
    if (term === this.searchTerm) {
      return;
    }
    this.searchTerm = term;
    this.isSearching = term !== '';
    this.pageIndex = 1;
    this.rows = [];
    if (this.tidyTable) {
      this.tidyTable.isLoading = true;
    }
    try {
      await this.loadTable(false);
    } finally {
      if (this.tidyTable) {
        this.tidyTable.isLoading = false;
      }
    }
  }

  async pageChange(_page) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.isGettingLists) {
      return;
    }
    if (this.tidyTable) {
      this.tidyTable.isLoading = true;
    }
    this.isGettingLists = true;
    this.pageIndex += 1;
    try {
      await this.loadTable(false);
    } finally {
      if (this.tidyTable) {
        this.tidyTable.isLoading = false;
      }
      this.isGettingLists = false;
    }
  }

  exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.tidyTable) {
      this.tidyTable.exportCsv(this.tidyTable.tabledataSource);
    } else {
      this.util.showError('No data to export', 5000);
    }
  }

}
